import {filter, find, isEmpty, map, sortBy} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, CopyText, InformationModal, ParticipantInfo, SessionSchedule} from 'components';
import {UserContext} from 'context';
import {SocketEvent} from 'enums';
import {useObservable, useSocketEventCallback} from 'hooks';
import {OnlineSession} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';
import {sessionUtils} from 'utils';

const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.APPOINTMENT_REMINDER;

type SessionReminderEvent = SocketEvent.AppointmentSoon | SocketEvent.AppointmentStarted | SocketEvent.SessionParticipantInvited;

type SessionReminder = {
    session: OnlineSession;
    event: SessionReminderEvent;
}

const UPDATE_TIMER_INTERVAL = 1000 * 10;

export const AppointmentReminder: React.FC = () => {
    const {t} = useTranslation();
    const {localUser} = useObservable(authDataStore);
    const {socket, settings: {common}} = useContext(UserContext);
    const [selectedSessionId, setSelectedSessionId] = useState<string>();
    const [sessionReminders, setSessionReminders] = useState<SessionReminder[]>([]);
    const [startIn, setStartIn] = useState<number>();

    const selectedSessionReminder = useMemo(() =>
        find(sessionReminders, (reminder) => reminder.session.id === selectedSessionId),
    [sessionReminders, selectedSessionId]);

    const isStarted = useMemo(() => selectedSessionReminder?.event === SocketEvent.AppointmentStarted, [selectedSessionReminder]);
    const isInvited = useMemo(() => selectedSessionReminder?.event === SocketEvent.SessionParticipantInvited, [selectedSessionReminder]);

    const getSessionStartIn = useCallback(() => {
        const startDate = selectedSessionReminder?.session.schedule?.startDate;
        if (!startDate) {
            return null;
        }

        const startAt = moment(startDate).tz(common.timeZone);
        const now = moment().tz(common.timeZone);
        const diffMinutes = startAt.diff(now, 'm');
        if (diffMinutes < 0) {
            return 0;
        } else {
            return diffMinutes;
        }
    }, [common.timeZone, selectedSessionReminder?.session.schedule?.startDate]);

    const title = useMemo(() => {
        if (isInvited) {
            return t(T_KEYS.TITLE_INVITED);
        }
        if (isStarted) {
            return t(T_KEYS.TITLE_STARTED);
        }
        return t(T_KEYS.TITLE_START_SOON);
    }, [isInvited, isStarted, t]);

    const handleAddSession = useCallback((session: OnlineSession, event: SessionReminderEvent) => {
        setSessionReminders((prev) => {
            const updated = filter(prev, (reminder) => reminder.session.id !== session.id);
            return [...updated, {session, event}];
        });

        if (!selectedSessionId) {
            setSelectedSessionId(session.id);
        }
    }, [selectedSessionId]);

    useSocketEventCallback(socket, SocketEvent.AppointmentSoon, useCallback((session: OnlineSession) => {
        handleAddSession(session, SocketEvent.AppointmentSoon);
    }, [handleAddSession]));

    useSocketEventCallback(socket, SocketEvent.AppointmentStarted, useCallback((session: OnlineSession) => {
        handleAddSession(session, SocketEvent.AppointmentStarted);
    }, [handleAddSession]));

    useSocketEventCallback(socket, SocketEvent.SessionParticipantInvited, useCallback((session: OnlineSession) => {
        handleAddSession(session, SocketEvent.SessionParticipantInvited);
    }, [handleAddSession]));

    const handleModalClose = useCallback(() => {
        setSessionReminders((prev) => filter(prev, (reminder) => reminder.session.id !== selectedSessionId));
        setSelectedSessionId(undefined);
    }, [selectedSessionId]);

    const goToJoinSessionPage = useCallback(() => {
        if (selectedSessionReminder?.session) {
            sessionUtils.goToJoinSessionPage(selectedSessionReminder.session);
        }
        handleModalClose();

    }, [selectedSessionReminder, handleModalClose]);

    useEffect(() => {
        if (!isEmpty(sessionReminders) && !selectedSessionId) {
            // close modal and open again
            setTimeout(() => {
                setSelectedSessionId(sessionReminders[0].session.id);
            }, 300);
        }
    }, [sessionReminders, selectedSessionId]);

    useEffect(() => () => {
        setSelectedSessionId(undefined);
        setSessionReminders([]);
    }, []);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        if (selectedSessionReminder?.event === SocketEvent.AppointmentSoon) {
            setStartIn(getSessionStartIn());
            intervalId = setInterval(() => {
                setStartIn(getSessionStartIn());
            }, UPDATE_TIMER_INTERVAL);
        }

        return () => {
            clearInterval(intervalId);
        };
    }, [getSessionStartIn, selectedSessionReminder]);

    return (
        <InformationModal
            title={title}
            open={!!selectedSessionReminder}
            fullWidth
            maxWidth='xs'
            onClose={handleModalClose}
        >
            <div className='tw-ts-h3-bold tw-text-default-dimmed tw-text-center'>{(selectedSessionReminder?.session as any)?.name}</div>
            {!!startIn && !isStarted &&
                <div className='tw-my-4 tw-text-center tw-ts-h5-bold tw-text-default-dimmed'>
                    <span>{t(T_KEYS.STARTS_IN)}</span>
                    <span className='tw-px-1'>{startIn}</span>
                    <span>{t(T_KEYS_COMMON.ALL.MINUTES_SHORT)}</span>
                </div>}
            <div className='tw-flex tw-justify-center'>
                <CopyText
                    text={selectedSessionId}
                    labelPlacement='left'
                    iconColor='default-dimmed'
                    className='tw-text-default-dimmed'
                />
            </div>
            <div className='tw-flex tw-flex-col tw-items-center'>
                {!isInvited && selectedSessionReminder?.session.schedule &&
                    <SessionSchedule
                        session={selectedSessionReminder.session}
                        timeZone={common.timeZone}
                        dateFormat={common.dateFormat}
                        timeFormat={common.timeFormat}
                        className='tw-flex tw-ts-h5 tw-text-default-dimmed tw-justify-center tw-mt-4'
                    />}

                <div className='tw-p-2 tw-border tw-border-default tw-mt-4 tw-w-full tw-text-center'>
                    <div className='tw-ts-h5-bold tw-text-default-dimmed'>
                        {t(T_KEYS_COMMON.SESSION.LABELS.PARTICIPANTS)}
                    </div>
                    {map(sortBy(selectedSessionReminder?.session?.participants, 'role'), (participant) => (
                        <ParticipantInfo key={participant.id} participant={participant} local={participant.data?.eid === localUser.eid} />
                    ))}
                </div>
            </div>
            <div className='tw-mt-4 tw-text-center'>
                <Button
                    color={isStarted || isInvited ? 'success' : 'primary'}
                    animated={isStarted || isInvited}
                    disabled={!socket?.connected}
                    onClick={goToJoinSessionPage}
                >
                    {t(T_KEYS.OPEN_SESSION_BTN)}
                </Button>
            </div>
        </InformationModal>);
};